import { useCallback } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { publicEnvConfig } from 'config/public';
import {
	fetchDpApiData,
	fetchDpApiResponse,
	fetchDpApiResult,
	type FetchError,
	type FetchResponse,
} from 'utils/fetch-new';
import { ignorePromiseRejection } from 'utils/helpers';
import type { Result } from 'utils/result';

import {
	buildEndpointKey,
	buildEndpointRequestOptions,
	buildEndpointUrl,
	type EndpointBody as BodyOf,
	type EndpointInput,
	type EndpointInputName,
	type EndpointResponse,
	type EndpointUrlInput,
	parseEndpointKey as parseKey,
} from '../helpers';

import { type DpApiEndpoints, dpApiEndpoints } from './definitions';

export const TEST_DP_API_URL = 'https://api.test/v1/';
export const DP_API_URL: string =
	(process.env.NODE_ENV === 'test'
		? TEST_DP_API_URL
		: publicEnvConfig.DIGITAL_PLATFORM_API_URL_SEGMENT) ?? '/';

export type DpApiEndpointInput = EndpointInput<DpApiEndpoints>;
export type DpApiEndpointUrlInput = EndpointUrlInput<DpApiEndpoints>;

/** Request body type for a Digital Platform endpoint. */
export type DpApiEndpointBody<Name extends keyof DpApiEndpoints> = BodyOf<
	DpApiEndpoints,
	Name
>;

type NameOf<T> = EndpointInputName<DpApiEndpoints, T>;
type ResponseOf<T> = EndpointResponse<DpApiEndpoints, NameOf<T>>;

/** Get a Digital Platform endpoint URL. */
export function getDpApiEndpointUrl(input: DpApiEndpointUrlInput) {
	return buildEndpointUrl<DpApiEndpoints>(DP_API_URL, dpApiEndpoints, input);
}

/**
 * Do a request to a Digital Platform endpoint and get the response, status
 * and headers.
 *
 * A non-ok status resolves rather than throws, so a caller that cares about
 * the status gets it without catching. Validation errors arrive as plain data
 * here.
 */
export function fetchDpApiEndpointResponse<T extends DpApiEndpointInput>(
	input: T,
): Promise<FetchResponse<ResponseOf<T>>> {
	return fetchDpApiResponse<ResponseOf<T>>(
		getDpApiEndpointUrl(input),
		buildEndpointRequestOptions<DpApiEndpoints>(dpApiEndpoints, input),
	);
}

/** Do a request to a Digital Platform endpoint and get the parsed response.*/
export function fetchDpApiEndpointData<T extends DpApiEndpointInput>(
	input: T,
): Promise<ResponseOf<T>> {
	return fetchDpApiData<ResponseOf<T>>(
		getDpApiEndpointUrl(input),
		buildEndpointRequestOptions<DpApiEndpoints>(dpApiEndpoints, input),
	) as Promise<ResponseOf<T>>;
}

/** Do a request to a Digital Platform endpoint and get a result type. */
export function fetchDpApiEndpointResult<T extends DpApiEndpointInput>(
	input: T,
): Promise<Result<ResponseOf<T>, FetchError>> {
	return fetchDpApiResult<ResponseOf<T>>(
		getDpApiEndpointUrl(input),
		buildEndpointRequestOptions<DpApiEndpoints>(dpApiEndpoints, input),
	) as Promise<Result<ResponseOf<T>, FetchError>>;
}

/** Get an SWR key for a Digital Platform endpoint. */
export function getDpApiEndpointKey<T extends DpApiEndpointInput>(input: T) {
	return buildEndpointKey<DpApiEndpoints, T>(dpApiEndpoints, input);
}

interface UseDpApiEndpointOptions {
	isActive?: boolean;
	isImmutable?: boolean;
	keepPreviousData?: boolean;
}

/** Use SWR with a Digital Platform endpoint. */
export function useDpApiEndpoint<T extends DpApiEndpointInput>(
	input: T | null,
	{
		isActive = true,
		isImmutable = true,
		keepPreviousData = false,
	}: UseDpApiEndpointOptions = {},
) {
	const useSWRHook = isImmutable ? useSWRImmutable : useSWR;
	const { data, error, isLoading, isValidating, mutate } = useSWRHook(
		isActive && input ? getDpApiEndpointKey(input) : null,
		(key) =>
			fetchDpApiEndpointData(parseKey<DpApiEndpoints, T>(dpApiEndpoints, key)),
		{ errorRetryCount: 2, keepPreviousData },
	);

	const invalidateCache = useCallback(() => {
		ignorePromiseRejection(mutate());
	}, [mutate]);

	return {
		data,
		error: error as FetchError | undefined,
		invalidateCache,
		isLoading,
		isValidating,
	};
}
